import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArchiveBoxIcon,
  TrophyIcon,
  HeartIcon,
  PhotoIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'

export default function LegacyPage() {
  const legacySections = [
    {
      title: 'Historic Roster',
      description: 'View member attendance records across all years',
      icon: ChartBarIcon,
      href: '/legacy/roster',
      color: 'bg-blue-500',
      stats: 'Track attendance since 2007'
    },
    {
      title: 'Kazy Cup Champions',
      description: 'Annual award winners and their achievements',
      icon: TrophyIcon,
      href: '/legacy/kazy-cup',
      color: 'bg-yellow-500',
      stats: 'Honoring excellence since 2008'
    },
    {
      title: 'Memorial Section',
      description: 'Remembering members who have passed',
      icon: HeartIcon,
      href: '/legacy/memorial',
      color: 'bg-red-500',
      stats: 'Forever in our hearts'
    },
    {
      title: 'Photo Gallery',
      description: 'Pictures from years past, organized by event and year',
      icon: PhotoIcon,
      href: '/legacy/gallery',
      color: 'bg-purple-500',
      stats: 'Memories captured in time'
    },
    {
      title: 'Railbird Games',
      description: 'Traditional gambling games, rules, and tales from the rail',
      icon: RectangleGroupIcon,
      href: '/legacy/railbird-games',
      color: 'bg-amber-600',
      stats: 'Stories and strategies passed down'
    },
    {
      title: 'The Railbird Ledger',
      description: 'Adventures, stories, and memorable moments from the rails',
      icon: BookOpenIcon,
      href: '/legacy/ledger',
      color: 'bg-emerald-600',
      stats: 'Chronicles of railbird adventures'
    }
  ]

  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-primary-600 rounded-full">
              <ArchiveBoxIcon className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Legacy & History
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Preserving the rich tradition of Midwest Railbirds since 2007. 
            Explore our history, honor our champions, and remember those who made this community special.
          </p>
        </motion.div>


        {/* Legacy Sections Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {legacySections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to={section.href}
                className="block bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 ${section.color} rounded-lg`}>
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                    {section.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {section.stats}
                    </span>
                    <span className="text-primary-600 font-medium group-hover:translate-x-1 transition-transform">
                      Explore →
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </div>
  )
}
